/**
 * "Policy evaluation" section (docs/plan/mail-scanner.md §14 E0-U): which rules the scanner evaluated, the disposition
 * it chose and why, and each effect with its status. ONE component and ONE wording for the admin message trace, the
 * admin quarantine detail and the webmail message details. `mode="user"` is the simplified projection: no rule
 * ids or guard codes, only rules that matched/failed with a display name, outcome and user-safe reasons. Input is the
 * value-free observation DTO emitted by gxscan (no content, no file names).
 */
import { useI18n, type MessageKey } from '@platform/i18n';
import { Alert } from '../components/misc';
import { StatusBadge, type Status } from '../composites/states';

export interface PolicyEffectEvidence { type: string; status: string }
export interface PolicyRuleEvidence {
  rule_id: string;
  filter: string;
  exception?: string;
  outcome: string;
  actions?: readonly PolicyEffectEvidence[];
  effects?: readonly PolicyEffectEvidence[];
}
/** Value-free message authentication results (SPF/DKIM/DMARC) the rules were evaluated with. */
export interface PolicyAuthEvidence {
  source: string;
  spf: string;
  dkim: string;
  dmarc: string;
  dmarc_policy: string;
  from_aligned: string;
}
/** DNS blocklist lookups of the message: counters and the listings (the listed address or domain is admin-visible). */
export interface PolicyDnsblEvidence {
  queried: number;
  unknown: number;
  truncated?: boolean;
  hits?: readonly { zone: string; key_type: string; key: string; label: string }[];
}
export interface PolicyEvaluationEvidence {
  disposition: string;
  av_verdict?: string;
  decided_by?: string;
  guard?: string;
  rules: readonly PolicyRuleEvidence[];
  truncated?: boolean;
  auth?: PolicyAuthEvidence;
  dnsbl?: PolicyDnsblEvidence;
}

const OUTCOMES = ['no_match', 'excepted', 'unresolved', 'matched', 'test_mode', 'skipped_allow', 'transformation_failed'] as const;
const DISPOSITIONS = ['permit', 'block', 'reject', 'defer', 'discard', 'quarantine'] as const;
const EFFECT_STATUSES = ['planned', 'applied', 'skipped', 'failed'] as const;
/** Guard reason codes emitted by gxscan; anything else falls back to the generic wording. */
const GUARDS = ['trigger_unavailable', 'clock_unavailable', 'analyzer_unavailable', 'facts_unavailable', 'malformed_message', 'organization_scope_unavailable', 'recipient_split_required'] as const;
const EFFECT_TYPES = ['add_header', 'remove_header', 'header_add', 'header_set', 'header_remove', 'notify', 'remove_attachment', 'subject_prefix', 'connection', 'unpack_zip', 'pack_zip', 'convert_pdf', 'repack', 'remove_encryption', 'banner', 'copy_to_quarantine', 'copy_attachment_to_quarantine', 'quarantine', 'reject', 'delete', 'defer'] as const;
const TRI = ['true', 'false', 'unknown', 'not_evaluated'] as const;

/** Pseudo rule gxscan records when the mailbox owner's own list decided (apps/gxscan/internal/rules/personal.go). */
export const PERSONAL_RULE_ID = 'personal-list';

const known = <T extends string>(list: readonly T[], v: unknown): v is T => typeof v === 'string' && (list as readonly string[]).includes(v);

export const OUTCOME_TONE: Readonly<Record<(typeof OUTCOMES)[number], Status>> = {
  matched: 'warning', no_match: 'inactive', excepted: 'unknown', unresolved: 'error', test_mode: 'unknown', skipped_allow: 'unknown', transformation_failed: 'error',
};
export const EFFECT_TONE: Readonly<Record<(typeof EFFECT_STATUSES)[number], Status>> = {
  applied: 'ok', planned: 'unknown', skipped: 'inactive', failed: 'error',
};

/** Code to translation-key map, the single wording source for every surface. */
export const policyKeys = {
  outcome: (v: string): MessageKey => (known(OUTCOMES, v) ? `policyEvidence.outcome.${v}` : 'policyEvidence.outcome.unknown'),
  disposition: (v: string): MessageKey => (known(DISPOSITIONS, v) ? `policyEvidence.disposition.${v}` : 'policyEvidence.disposition.unknown'),
  effectType: (v: string): MessageKey => (known(EFFECT_TYPES, v) ? `policyEvidence.effect.${v}` : 'policyEvidence.effect.unknown'),
  effectStatus: (v: string): MessageKey => (known(EFFECT_STATUSES, v) ? `policyEvidence.effectStatus.${v}` : 'policyEvidence.effectStatus.unknown'),
  guard: (v: string): MessageKey => (known(GUARDS, v) ? `policyEvidence.guard.${v}` : 'policyEvidence.guard.unknown'),
  tri: (v: string): MessageKey => (known(TRI, v) ? `policyEvidence.tri.${v}` : 'policyEvidence.tri.unknown'),
};

export interface PolicyEvaluationViewProps {
  evaluation?: PolicyEvaluationEvidence;
  /** Evidence was recorded but dropped (size/validation): shown as unavailable, never reconstructed. */
  unavailable?: boolean;
  /** `admin` shows every rule and technical identifiers; `user` is the simplified projection. */
  mode?: 'admin' | 'user';
  /** Display names by rule id; the user projection only shows rules that have one. */
  ruleNames?: Readonly<Record<string, string>>;
  /** Rules hash / policy revision, admin only. */
  revision?: string;
}

export function PolicyEvaluationView({ evaluation, unavailable, mode = 'admin', ruleNames, revision }: PolicyEvaluationViewProps) {
  const { t } = useI18n();
  const admin = mode === 'admin';
  if (!evaluation || unavailable) {
    return <p className="text-sm text-muted-foreground">{t('policyEvidence.unavailable')}</p>;
  }
  /** Display name; in the admin view a rule missing from a readable rule set is marked deleted, else its id shows. */
  const label = (id: string): string | undefined => {
    if (id === PERSONAL_RULE_ID) return t('policyEvidence.personalList');
    const name = ruleNames?.[id];
    if (name !== undefined) return name;
    return admin ? (ruleNames ? t('policyEvidence.ruleDeleted', { rule: id }) : id) : undefined;
  };
  const rules = evaluation.rules.filter((r) => admin || (r.outcome !== 'no_match' && (ruleNames?.[r.rule_id] !== undefined || r.rule_id === PERSONAL_RULE_ID)));
  const decider = evaluation.decided_by ? label(evaluation.decided_by) : undefined;
  return (
    <div className="flex min-w-0 flex-col gap-3" data-testid="policy-evaluation">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{t('policyEvidence.dispositionLabel')}</span>
        <StatusBadge status={evaluation.disposition === 'permit' ? 'ok' : 'warning'} label={t(policyKeys.disposition(evaluation.disposition))} />
        {evaluation.guard && (
          <span className="text-sm">
            {t(policyKeys.guard(evaluation.guard))}
            {admin && <span className="ml-1 font-mono text-xs text-muted-foreground">({evaluation.guard})</span>}
          </span>
        )}
      </div>
      {admin && evaluation.auth && <AuthEvidence auth={evaluation.auth} />}
      {admin && evaluation.dnsbl && <DnsblEvidence dnsbl={evaluation.dnsbl} />}
      {decider && <p className="break-all text-sm">{t('policyEvidence.decidedBy', { rule: decider })}</p>}
      {admin && revision && <p className="break-all font-mono text-xs text-muted-foreground">{t('policyEvidence.revision')}: {revision}</p>}
      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('policyEvidence.noRules')}</p>
      ) : (
        <ul className="flex flex-col gap-2" aria-label={t('policyEvidence.rules')}>
          {rules.map((rule) => (
            <li key={rule.rule_id} className="min-w-0 rounded-md border p-2">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className={ruleNames?.[rule.rule_id] || rule.rule_id === PERSONAL_RULE_ID ? 'break-words text-sm font-medium' : 'break-all font-mono text-xs'}>{label(rule.rule_id) ?? rule.rule_id}</span>
                <StatusBadge status={known(OUTCOMES, rule.outcome) ? OUTCOME_TONE[rule.outcome] : 'unknown'} label={t(policyKeys.outcome(rule.outcome))} />
                {admin && (
                  <span className="text-xs text-muted-foreground">
                    {t('policyEvidence.filter', { value: t(policyKeys.tri(rule.filter)) })}
                    {rule.exception !== undefined && ` · ${t('policyEvidence.exception', { value: t(policyKeys.tri(rule.exception)) })}`}
                  </span>
                )}
              </div>
              {(rule.actions ?? rule.effects ?? []).length > 0 && (
                <ul className="mt-1 flex flex-col gap-1" aria-label={t('policyEvidence.effects')}>
                  {(rule.actions ?? rule.effects ?? []).map((effect, i) => (
                    <li key={`${effect.type}:${i}`} className="flex flex-wrap items-center gap-2 text-xs">
                      <span>{t(policyKeys.effectType(effect.type))}</span>
                      <StatusBadge status={known(EFFECT_STATUSES, effect.status) ? EFFECT_TONE[effect.status] : 'unknown'} label={t(policyKeys.effectStatus(effect.status))} />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      {evaluation.truncated === true && <Alert variant="info">{t('policyEvidence.truncated')}</Alert>}
    </div>
  );
}

const AUTH_SOURCES = ['upstream', 'own', 'none'] as const;

/** One line per message: the authentication results behind the auth_* conditions; unknown is never shown as a pass. */
function AuthEvidence({ auth }: { auth: PolicyAuthEvidence }) {
  const { t } = useI18n();
  const value = (v: string): string => (v === 'unknown' ? t('policyEvidence.auth.unknown') : v === 'absent' ? t('policyEvidence.auth.absent') : v);
  const aligned = auth.from_aligned === 'true' ? t('policyEvidence.auth.yes') : auth.from_aligned === 'false' ? t('policyEvidence.auth.no') : t('policyEvidence.auth.unknown');
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid="policy-auth">
      <p className="text-sm font-medium">{t('policyEvidence.auth.title')}</p>
      <p className="break-words text-sm">{t('policyEvidence.auth.summary', { spf: value(auth.spf), dkim: value(auth.dkim), dmarc: value(auth.dmarc), policy: value(auth.dmarc_policy), aligned })}</p>
      <p className="text-xs text-muted-foreground">{t(known(AUTH_SOURCES, auth.source) ? `policyEvidence.auth.source.${auth.source}` : 'policyEvidence.auth.source.none')}</p>
    </div>
  );
}

/** The blocklist lookups behind a dnsbl_listed condition: how many were asked, how many went unanswered, and what is listed. */
function DnsblEvidence({ dnsbl }: { dnsbl: PolicyDnsblEvidence }) {
  const { t } = useI18n();
  const hits = dnsbl.hits ?? [];
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid="policy-dnsbl">
      <p className="text-sm font-medium">{t('policyEvidence.dnsbl.title')}</p>
      <p className="break-words text-sm">{t('policyEvidence.dnsbl.summary', { queried: dnsbl.queried, unknown: dnsbl.unknown })}</p>
      {hits.length === 0 && dnsbl.unknown === 0 && <p className="text-xs text-muted-foreground">{t('policyEvidence.dnsbl.none')}</p>}
      {hits.map((hit, index) => (
        <p key={index} className="break-all text-sm">{t('policyEvidence.dnsbl.hit', { key: hit.key, kind: t(hit.key_type === 'domain' ? 'policyEvidence.dnsbl.kind.domain' : 'policyEvidence.dnsbl.kind.ip'), zone: hit.zone, label: hit.label })}</p>
      ))}
      {dnsbl.truncated && <p className="text-xs text-muted-foreground">{t('policyEvidence.dnsbl.truncated')}</p>}
    </div>
  );
}
